import React, { Component } from 'react';
import './ThoughtBubble.css';

class ThoughtBubble extends Component {
  constructor(props) {
    super(props);
    
    // Generate unique properties for each bubble
    const uniqueId = Math.random().toString(36).substr(2, 9);
    const randomHue = Math.floor(Math.random() * 360);
    const randomSize = 0.8 + Math.random() * 0.4; // 0.8 to 1.2 scale
    const randomFloatDelay = Math.random() * 1000 + 500; // 0.5-1.5 seconds
    const randomFloatDuration = 45 + Math.random() * 30; // 45-75 seconds
    
    this.state = {
      isAnimating: false,
      shouldRemove: false,
      isFadingOut: false,
      opacity: 0,
      scale: 0.8,
      position: props.initialPosition || { x: 0, y: 0 },
      animationStartTime: null,
      floatStartTime: null,
      uniqueId,
      randomHue,
      randomSize,
      randomFloatDelay,
      randomFloatDuration,
      isHovered: false
    };
    
    this.animationRef = React.createRef();
    this.floatTimer = null;
    this.fadeInTimer = null;
    this.removalTimer = null;
    this.hoverTimer = null;
  }

  componentDidMount() {
    // Start fade-in animation immediately
    this.startFadeIn();
    
    // Check if this is the first bubble (no delay for first bubble)
    const isFirstBubble = this.props.isFirstBubble;
    
    if (isFirstBubble) {
      // First bubble starts floating immediately
      this.startFloating();
    } else {
      // Other bubbles start floating after the unique delay
      this.floatTimer = setTimeout(() => {
        this.startFloating();
      }, this.state.randomFloatDelay);
    }
  }

  componentDidUpdate(prevProps) {
    // Check if the bubble should start fading out
    if (this.props.shouldFadeOut && !prevProps.shouldFadeOut && !this.state.isFadingOut) {
      this.startFadeOut();
    }
  }

  componentWillUnmount() {
    // Clean up all timers
    if (this.floatTimer) clearTimeout(this.floatTimer);
    if (this.fadeInTimer) clearTimeout(this.fadeInTimer);
    if (this.removalTimer) clearTimeout(this.removalTimer);
    if (this.hoverTimer) clearTimeout(this.hoverTimer);
  }

  startFadeIn = () => {
    this.setState({ opacity: 1, scale: 1 });
  }

  startFloating = () => {
    this.setState({ 
      isAnimating: true, 
      floatStartTime: Date.now() 
    });
  }

  startFadeOut = () => {
    this.setState({ 
      isFadingOut: true,
      opacity: 0,
      scale: 0.8
    });
    
    // Remove the bubble after fade-out animation completes
    this.removalTimer = setTimeout(() => {
      this.setState({ shouldRemove: true });
      if (this.props.onBubbleComplete) {
        this.props.onBubbleComplete(this.props.thought.id);
      }
    }, 800); // Match the CSS transition duration
  }

  handleClick = () => {
    this.props.onBubbleClick(this.props.thought);
  }

  handleMouseEnter = () => {
    this.setState({ isHovered: true });
  }

  handleMouseLeave = () => {
    this.setState({ isHovered: false });
  }

  handleAnimationEnd = () => {
    // Add a small delay to ensure fade-out animation completes
    this.removalTimer = setTimeout(() => {
      this.setState({ shouldRemove: true });
      if (this.props.onBubbleComplete) {
        this.props.onBubbleComplete(this.props.thought.id);
      }
    }, 200);
  }

  getBubbleStyle = () => {
    const { 
      position, 
      isAnimating, 
      opacity, 
      scale, 
      randomHue, 
      randomSize, 
      randomFloatDuration,
      isHovered,
      isFadingOut
    } = this.state;
    
    const baseTransform = `scale(${scale * randomSize})`;
    const floatTransform = isAnimating ? 'translateY(-100vh)' : 'translateY(0)';
    const hoverTransform = isHovered && !isFadingOut ? 'scale(1.1)' : '';
    
    // First bubble should have no animation delay
    const isFirstBubble = this.props.isFirstBubble;
    const animationDelay = isFirstBubble ? '0s' : `${Math.random() * 2}s`;
    
    return {
      left: `${position.x}px`,
      top: `${position.y}px`,
      opacity: opacity,
      transform: `${baseTransform} ${floatTransform} ${hoverTransform}`,
      filter: `hue-rotate(${randomHue}deg)`,
      '--float-duration': `${randomFloatDuration}s`,
      animationDelay: animationDelay
    };
  }

  render() {
    const { thought } = this.props;
    const { shouldRemove, isAnimating, isFadingOut } = this.state;

    if (shouldRemove) return null;

    const bubbleClasses = [
      'thought-bubble',
      isAnimating ? 'floating' : '',
      isFadingOut ? 'fading-out' : ''
    ].filter(Boolean).join(' ');

    return (
      <div
        ref={this.animationRef}
        className={bubbleClasses}
        style={this.getBubbleStyle()}
        onClick={this.handleClick}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onAnimationEnd={this.handleAnimationEnd}
        data-bubble-id={this.state.uniqueId}
      >
        <div className="bubble-content">
          <span className="thought-text">{thought.thought || thought.text}</span>
          {thought.author && (
            <div className="thought-author">— {thought.author}</div>
          )}
          <div className="bubble-sparkle"></div>
        </div>
      </div>
    );
  }
}

export default ThoughtBubble;
